import { Check } from "lucide-react";
import { ConfettiElement } from "../components/ConfettiElement";
import { ProgressBar } from "../components/ProgressBar";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getSessionId, getToken } from "@/utils/storage";
import { getStoredGuestExamUser } from "@/api/exam";
import { createClient } from '@supabase/supabase-js';

// Supabase client
const supabaseUrl = 'https://nluntjftnyzlxhatvvks.supabase.co';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_API_KEY;

const ExamComplete = () => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [isProcessing, setIsProcessing] = useState(true);
  const navigate = useNavigate();

  const statusMessages = [
    "Reviewing your responses...",
    "Analyzing grammar and vocabulary...",
    "Evaluating fluency and pronunciation...",
    "Calculating your CEFR level...",
    "Finalizing your personalized results..."
  ];

  useEffect(() => {
    // Cycle through status messages
    const messageInterval = setInterval(() => {
      setCurrentMessageIndex((prev) => (prev + 1) % statusMessages.length);
    }, 2000);

    // Process results in background
    const processResults = async () => {
      try {
        const sessionId = getSessionId();
        
        // 1) Get tokens
        const paidToken = getToken();
        const guestUser = getStoredGuestExamUser();
        const guestToken = guestUser?.token ?? localStorage.getItem("guestToken");

        // 2) Decide which mode
        const isGuest = !paidToken && !!guestToken;
        const token = paidToken || guestToken;
        
        if (!sessionId || !token) {
          console.error('Session or authentication not found');
          setIsProcessing(false);
          return;
        }

        console.log('🎯 EXAM COMPLETE - Starting background processing...');
        
        // Wait 8 seconds for all tasks to complete
        await new Promise(resolve => setTimeout(resolve, 8000));
        
        // Call fluency analysis for Task 3
        try {
          console.log('🎯 EXAM COMPLETE - Calling fluency analysis...');
          
          // Get current task attempt ID from localStorage
          const currentAttemptId = localStorage.getItem('current_task_attempt_id');
          
          // Build fluency analysis payload with guest support
          const fluencyPayload: any = { session_id: sessionId };
          if (currentAttemptId) {
            fluencyPayload.task_attempt_id = currentAttemptId;
          }
          if (isGuest) {
            fluencyPayload.guest_id = guestUser?.user_id;
            fluencyPayload.is_guest = true;
          }
          
          const fluencyResponse = await fetch('https://nluntjftnyzlxhatvvks.supabase.co/functions/v1/fluency-pro', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(fluencyPayload)
          });

          if (fluencyResponse.ok) {
            const fluencyData = await fluencyResponse.json();
            console.log('✅ EXAM COMPLETE - Fluency analysis completed:', fluencyData);
          } else {
            console.warn('⚠️ EXAM COMPLETE - Fluency analysis failed:', fluencyResponse.status);
          }
        } catch (error) {
          console.error('❌ EXAM COMPLETE - Fluency analysis error:', error);
        }
        
        // Call final scoring function
        const scoringPayload: any = { session_id: sessionId };
        if (isGuest) {
          scoringPayload.guest_id = guestUser?.user_id;
          scoringPayload.is_guest = true;
        }
        
        const scoringResponse = await fetch('https://nluntjftnyzlxhatvvks.supabase.co/functions/v1/final-scoring-analysis', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(scoringPayload)
        });

        if (!scoringResponse.ok) {
          throw new Error(`Failed to calculate final scores: ${scoringResponse.status}`);
        }

        const scoringData = await scoringResponse.json();
        console.log('✅ EXAM COMPLETE - Final scoring completed:', scoringData);
        
        // Wait a bit more for database consistency
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Check if results are ready using Supabase client
        const supabase = createClient(supabaseUrl, supabaseAnonKey, {
          global: {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        });

        const { data: examData, error: examError } = await supabase
          .from('exams')
          .select('*')
          .eq('id', sessionId)
          .single();

        if (!examError && examData) {
          console.log('✅ EXAM COMPLETE - Results ready, redirecting...');
          setIsProcessing(false);
          navigate('/exam-results');
          return;
        }
        
        // If results not ready, wait a bit more and try again
        console.log('⏳ EXAM COMPLETE - Results not ready yet, waiting...');
        await new Promise(resolve => setTimeout(resolve, 3000));
        
        // Final attempt using Supabase client
        const { data: finalData, error: finalError } = await supabase
          .from('exams')
          .select('*')
          .eq('id', sessionId)
          .single();

        if (!finalError && finalData) {
          console.log('✅ EXAM COMPLETE - Results ready on final attempt, redirecting...');
          setIsProcessing(false);
          navigate('/exam-results');
          return;
        }
        
        // If still not ready, redirect anyway (results page will handle loading)
        console.log('⚠️ EXAM COMPLETE - Redirecting to results page (will handle loading)');
        setIsProcessing(false);
        navigate('/exam-results');
        
      } catch (error) {
        console.error('❌ EXAM COMPLETE - Error processing results:', error);
        setIsProcessing(false);
        // Redirect to results page anyway (it will handle the error)
        navigate('/exam-results');
      }
    };

    processResults();

    return () => {
      clearInterval(messageInterval);
    };
  }, [navigate, statusMessages.length]);

  return (
    <div className="bg-background flex flex-col items-center justify-center p-6 relative overflow-hidden">
      {/* Confetti Elements */}
      <ConfettiElement 
        type="rectangle" 
        color="confetti-blue" 
        className="absolute top-20 left-16 w-4 h-6 animate-float" 
      />
      <ConfettiElement 
        type="diamond" 
        color="confetti-green" 
        className="absolute top-32 left-80 w-3 h-3 animate-float-delayed" 
      />
      <ConfettiElement 
        type="rectangle" 
        color="confetti-yellow" 
        className="absolute top-28 right-32 w-5 h-3 animate-float" 
      />
      <ConfettiElement 
        type="oval" 
        color="confetti-light-blue" 
        className="absolute top-40 right-80 w-4 h-2 animate-float-delayed" 
      />
      <ConfettiElement 
        type="rectangle" 
        color="confetti-green" 
        className="absolute bottom-80 left-32 w-3 h-5 animate-float" 
      />
      <ConfettiElement 
        type="oval" 
        color="confetti-yellow" 
        className="absolute bottom-72 right-24 w-2 h-4 animate-float-delayed" 
      />
      <ConfettiElement 
        type="diamond" 
        color="confetti-blue" 
        className="absolute bottom-96 left-72 w-4 h-4 animate-float" 
      />
      <ConfettiElement 
        type="rectangle" 
        color="confetti-light-blue" 
        className="absolute top-52 left-96 w-3 h-4 animate-float-delayed" 
      />

      {/* Main Content */}
      <div className="flex flex-col items-center text-center max-w-2xl mx-auto">
        {/* Success Icon */}
        <div className="relative mb-12">
          <div className="w-32 h-32 rounded-full bg-success-light flex items-center justify-center">
            <Check className="w-16 h-16 text-success stroke-[3]" />
          </div>
        </div>

        {/* Title */}
        <h1 className="text-5xl md:text-6xl font-bold text-foreground mb-16">
          Exam complete!
        </h1>

        {/* Status Messages */}
        <div className="space-y-6 mb-16">
          <p className="text-lg md:text-xl text-muted-foreground">
            {statusMessages[currentMessageIndex]}
          </p>
        </div>

        {/* Progress Bar */}
        <ProgressBar />
      </div>
    </div>
  );
};

export default ExamComplete;